import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { socket } from "../../../common/socket";
import { deleteChatMessage, editChatMessage } from "../../../slices/chat/thunk";
import { updateMessages } from "../../../slices/chat/reducer";
import { showToast } from "../../../slices/toast/reducer";

export const useMessageActions = (selectedChatId, currentChat, currentUserId, currentMessagesRef) => {
  const dispatch = useDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [originalMessage, setOriginalMessage] = useState("");

  const handleDeleteMessage = useCallback((message) => {
    setSelectedMessage(message);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async (deleteFor) => {
    if (!selectedMessage) return;

    const chatId = selectedChatId || currentChat?.chatRequestId || currentChat?._id;
    if (!chatId) {
      dispatch(showToast({ type: "error", msg: "No chat selected" }));
      return;
    }

    setIsDeleting(true);

    try {
      const messageId = selectedMessage._id;

      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(messageId);
      if (!isValidObjectId) {
        throw new Error("Invalid message ID format");
      }

      const payload = {
        messageIds: [messageId],
        deleteFor: deleteFor
      };

      // Update local state immediately for optimistic UI update
      if (currentChat?.messages) {
        const currentMessages = currentMessagesRef.current || currentChat.messages || [];
        let updatedMessages;

        if (deleteFor === 'everyone') {
          // Mark as deleted for everyone - message stays visible
          updatedMessages = currentMessages.map(msg => {
            if (String(msg._id) === String(selectedMessage._id)) {
              return {
                ...msg,
                isDeleted: true,
                isDeleteEvery: true,
                isDeleteMe: false,
                content: "This message has been deleted",
                mediaUrl: null,
                fileName: null,
                messageType: 'text'
              };
            }
            return msg;
          });
        } else {
          // Delete for me only - remove from view
          updatedMessages = currentMessages.filter(msg =>
            String(msg._id) !== String(selectedMessage._id)
          );
        }

        dispatch(updateMessages({ messages: updatedMessages }));
        currentMessagesRef.current = updatedMessages;
      }

      const result = await dispatch(deleteChatMessage({ chatId, payload })).unwrap();

      dispatch(showToast({
        type: "success",
        msg: `${result.deletedCount} messages deleted ${deleteFor === 'everyone' ? 'for everyone' : 'for you'}`
      }));

      // Emit socket event for real-time updates
      socket.emit("messagesDeleted", {
        chatId,
        messageIds: [selectedMessage._id],
        deleteFor: deleteFor
      });

      setDeleteDialogOpen(false);
      setSelectedMessage(null);
    } catch (error) {
      console.error("Failed to delete message:", error);
      dispatch(showToast({
        type: "error",
        msg: error.message || "Failed to delete message"
      }));

      // Revert optimistic update on error
      if (currentChat?.messages) {
        dispatch(updateMessages({ messages: currentChat.messages }));
        currentMessagesRef.current = currentChat.messages;
      }
    } finally {
      setIsDeleting(false);
    }
  }, [selectedMessage, selectedChatId, currentChat, dispatch, currentMessagesRef]);

  const handleEditMessage = useCallback((message) => {
    setEditingMessage(message);
    const content = message?.content || "";
    setOriginalMessage(content);
    // Return content so parent can set it in input field
    return content;
  }, []);

  const handleSaveEdit = useCallback(async (newMessage) => {
    if (!editingMessage || !newMessage.trim()) return;

    // 🔍 DEBUG: Check the message ID format
    console.log("🔄 Editing message ID:", editingMessage._id);
    console.log("📝 Message ID type:", typeof editingMessage._id);
    console.log("🔢 Message ID length:", editingMessage._id?.length);
    console.log("✅ Valid ObjectId check:", /^[0-9a-fA-F]{24}$/.test(editingMessage._id));

    if (newMessage.trim() === originalMessage) {
      handleCancelEdit();
      return;
    }

    const chatId = selectedChatId || currentChat?._id;
    if (!chatId) return;

    try {
      // ✅ VALIDATE MESSAGE ID BEFORE SENDING
      const messageId = editingMessage._id;

      // Check if it's a valid MongoDB ObjectId (24 character hex string)
      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(messageId);

      if (!isValidObjectId) {
        console.error("❌ Invalid message ID format:", messageId);

        // Try to find the message by content if ID is invalid
        if (currentChat?.messages) {
          const foundMessage = currentChat.messages.find(msg =>
            msg.content === originalMessage &&
            String(msg.sender?._id) === String(currentUserId)
          );

          if (foundMessage && /^[0-9a-fA-F]{24}$/.test(foundMessage._id)) {
            console.log("🔍 Found valid message ID by content:", foundMessage._id);
            // Use the valid ID instead
            await dispatch(editChatMessage({
              chatId,
              messageId: foundMessage._id, // Use the valid ID
              content: newMessage.trim()
            })).unwrap();
          } else {
            throw new Error("Invalid message ID and cannot find valid alternative");
          }
        } else {
          throw new Error("Invalid message ID format");
        }
      } else {
        // Original valid ID flow
        await dispatch(editChatMessage({
          chatId,
          messageId: messageId,
          content: newMessage.trim()
        })).unwrap();
      }

      // ... rest of your success handling code
      setEditingMessage(null);
      setOriginalMessage("");

      return true;
    } catch (error) {
      console.error("Failed to edit message:", error);
      dispatch(showToast({ type: "error", msg: error.message || "Failed to edit message" }));
      return false;
    }
  }, [editingMessage, originalMessage, selectedChatId, currentChat, currentUserId, dispatch, currentMessagesRef]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
    setOriginalMessage("");
    return "";
  }, []);

  return {
    deleteDialogOpen,
    setDeleteDialogOpen,
    selectedMessage,
    setSelectedMessage,
    isDeleting,
    editingMessage,
    originalMessage,
    handleDeleteMessage,
    handleConfirmDelete,
    handleEditMessage,
    handleSaveEdit,
    handleCancelEdit
  };
};

